import React, { useEffect, useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { submitSignalBoxChangeRequest } from '../utils/siteUtils';
import MapBackground from './MapBackground';

// Types
interface SignalBox {
  id: number;
  name: string;
  internal_number: string | null;
  external_number: string | null;
}

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Styled components
const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  min-height: var(--app-height, 100vh);
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const Card = styled.div`
  background: rgba(20, 20, 20, 0.92);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 0;
  width: 95%;
  max-width: 480px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.06);
  z-index: 10;
  animation: ${fadeIn} 0.5s ease;
  overflow: hidden;

  @media (max-width: 480px) {
    max-height: 90vh;
  }
`;

const Header = styled.div`
  text-align: center;
  padding: 28px 28px 0;
  animation: ${fadeInUp} 0.6s ease;
`;

const PageIcon = styled.div`
  font-size: 32px;
  margin-bottom: 8px;
`;

const Title = styled.h1`
  color: white;
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 4px;
  letter-spacing: -0.3px;

  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const Subtitle = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  margin: 0;
`;

const SearchWrapper = styled.div`
  padding: 16px 28px 12px;
  position: relative;
  animation: ${fadeInUp} 0.4s ease 0.1s both;

  @media (max-width: 480px) {
    padding: 14px 20px 10px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 11px 14px 11px 38px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: white;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(255, 255, 255, 0.25);
  }

  &:focus {
    border-color: rgba(211, 47, 47, 0.5);
    background: rgba(255, 255, 255, 0.06);
    box-shadow: 0 0 0 3px rgba(211, 47, 47, 0.12);
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 42px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  pointer-events: none;
  color: rgba(255, 255, 255, 0.3);

  @media (max-width: 480px) {
    left: 34px;
  }
`;

const ResultCount = styled.div`
  padding: 0 28px 4px;
  color: rgba(255, 255, 255, 0.3);
  font-size: 11px;
  font-weight: 500;

  @media (max-width: 480px) {
    padding: 0 20px 4px;
  }
`;

const ListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 28px 20px;

  @media (max-width: 480px) {
    padding: 0 20px 16px;
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
`;

const ItemCard = styled.div<{ delay: number }>`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 6px;
  transition: all 0.2s;
  animation: ${fadeInUp} 0.3s ease ${props => Math.min(props.delay * 0.02, 0.4)}s both;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
`;

const ItemName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  flex: 1;
  min-width: 0;
`;

const SuggestBtn = styled.button`
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.4);
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 8px;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    color: #ef5350;
    border-color: rgba(239, 83, 80, 0.4);
    background: rgba(239, 83, 80, 0.08);
  }
`;

const PhoneRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const PhoneItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PhoneLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(255, 255, 255, 0.3);
`;

const PhoneNumber = styled.a`
  font-size: 13px;
  color: #ef5350;
  text-decoration: none;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  transition: color 0.2s;

  &:hover {
    color: #ff7043;
    text-decoration: underline;
  }
`;

const NoNumber = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.2);
  font-style: italic;
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.05);
  margin: 0 28px;

  @media (max-width: 480px) {
    margin: 0 20px;
  }
`;

const Footer = styled.div`
  padding: 16px 28px 20px;

  @media (max-width: 480px) {
    padding: 14px 20px 16px;
  }
`;

const BackButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  -webkit-tap-highlight-color: transparent;
  animation: ${fadeInUp} 0.5s ease 0.3s both;

  &:hover {
    background: rgba(255, 255, 255, 0.07);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 20px;
  gap: 14px;
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #d32f2f;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const LoadingText = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 50px 20px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  animation: ${fadeIn} 0.3s ease;
`;

const EmptyIcon = styled.div`
  font-size: 32px;
  margin-bottom: 10px;
`;

const EmptyText = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
  font-weight: 500;
`;

const EmptyHint = styled.div`
  color: rgba(255, 255, 255, 0.25);
  font-size: 12px;
  margin-top: 4px;
`;

// ================================
// Modal styled components
// ================================
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalCard = styled.div`
  background: #1a1a1a;
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.08);
  animation: ${fadeInUp} 0.3s ease;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, #d32f2f, #b71c1c);
  padding: 18px 20px;
`;

const ModalTitle = styled.h3`
  color: white;
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
`;

const ModalSubtitle = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(255, 255, 255, 0.4);
  margin-bottom: 6px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: white;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  outline: none;
  box-sizing: border-box;
  transition: all 0.2s;

  &::placeholder {
    color: rgba(255, 255, 255, 0.2);
  }

  &:focus {
    border-color: rgba(211, 47, 47, 0.5);
    background: rgba(255, 255, 255, 0.06);
    box-shadow: 0 0 0 3px rgba(211, 47, 47, 0.1);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: white;
  font-size: 13px;
  outline: none;
  box-sizing: border-box;
  transition: all 0.2s;
  resize: vertical;
  min-height: 60px;
  font-family: inherit;

  &::placeholder {
    color: rgba(255, 255, 255, 0.2);
  }

  &:focus {
    border-color: rgba(211, 47, 47, 0.5);
    background: rgba(255, 255, 255, 0.06);
    box-shadow: 0 0 0 3px rgba(211, 47, 47, 0.1);
  }
`;

const CurrentValue = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  margin-bottom: 6px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  padding-top: 4px;
`;

const SubmitButton = styled.button`
  flex: 1;
  padding: 11px 16px;
  border-radius: 8px;
  border: none;
  background: #d32f2f;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #e53935;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 11px 16px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const SuccessMessage = styled.div`
  background: rgba(46, 160, 67, 0.15);
  border: 1px solid rgba(46, 160, 67, 0.3);
  color: #4ade80;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  text-align: center;
  margin-bottom: 12px;
  animation: ${fadeIn} 0.3s ease;
`;

const ErrorBanner = styled.div`
  background: rgba(211, 47, 47, 0.15);
  border: 1px solid rgba(211, 47, 47, 0.3);
  color: #ef5350;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  text-align: center;
  margin-bottom: 12px;
  animation: ${fadeIn} 0.3s ease;
`;

// Component
interface SignalBoxDirectoryProps {
  onBack: () => void;
}

const SignalBoxDirectory: React.FC<SignalBoxDirectoryProps> = ({ onBack }) => {
  const { user } = useAuth();
  const [signalBoxes, setSignalBoxes] = useState<SignalBox[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Suggest change modal state
  const [editingBox, setEditingBox] = useState<SignalBox | null>(null);
  const [suggestedInternal, setSuggestedInternal] = useState('');
  const [suggestedExternal, setSuggestedExternal] = useState('');
  const [changeNotes, setChangeNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => {
      const appHeight = window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${appHeight}px`);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!supabase) {
          setError('Database not connected');
          setLoading(false);
          return;
        }
        const { data, error: fetchError } = await supabase
          .from('signal_boxes')
          .select('*')
          .order('name', { ascending: true });

        if (fetchError) throw fetchError;
        setSignalBoxes(data || []);
      } catch (err: any) {
        console.error('Error fetching signal boxes:', err);
        setError(err.message || 'Failed to load signal boxes');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return signalBoxes;
    const q = search.toLowerCase().trim();
    return signalBoxes.filter(
      (box) =>
        box.name.toLowerCase().includes(q) ||
        (box.internal_number && box.internal_number.includes(q)) ||
        (box.external_number && box.external_number.includes(q))
    );
  }, [signalBoxes, search]);

  const formatPhone = (num: string) => {
    return `tel:${num.replace(/\s+/g, '')}`;
  };

  const openSuggestModal = (box: SignalBox) => {
    setEditingBox(box);
    setSuggestedInternal(box.internal_number || '');
    setSuggestedExternal(box.external_number || '');
    setChangeNotes('');
    setSubmitSuccess(false);
    setSubmitError(null);
  };

  const closeSuggestModal = () => {
    setEditingBox(null);
    setSuggestedInternal('');
    setSuggestedExternal('');
    setChangeNotes('');
    setSubmitSuccess(false);
    setSubmitError(null);
  };

  const handleSubmitChange = async () => {
    if (!editingBox || !user?.email) return;

    // Check that something actually changed
    const internalChanged = suggestedInternal !== (editingBox.internal_number || '');
    const externalChanged = suggestedExternal !== (editingBox.external_number || '');

    if (!internalChanged && !externalChanged) {
      setSubmitError('No changes detected. Please update at least one number.');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);

      await submitSignalBoxChangeRequest({
        signal_box_id: editingBox.id,
        signal_box_name: editingBox.name,
        user_email: user.email,
        suggested_internal: suggestedInternal || undefined,
        suggested_external: suggestedExternal || undefined,
        notes: changeNotes || undefined,
        status: 'pending',
      });

      setSubmitSuccess(true);

      // Auto-close after a delay
      setTimeout(() => {
        closeSuggestModal();
      }, 2000);
    } catch (err: any) {
      console.error('Error submitting change request:', err);
      setSubmitError(err.message || 'Failed to submit change request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <MapBackground interactive={true} />
      <Container>
        <Card>
          <Header>
            <PageIcon>📞</PageIcon>
            <Title>Signal Box Directory</Title>
            <Subtitle>
              {loading ? 'Loading...' : 'A directory of phone numbers'}
            </Subtitle>
          </Header>

          <SearchWrapper>
            <SearchIcon>🔍</SearchIcon>
            <SearchInput
              placeholder="Search by name or number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </SearchWrapper>

          {search.trim() && (
            <ResultCount>
              {filtered.length} result{filtered.length !== 1 ? 's' : ''} found
            </ResultCount>
          )}

          <ListContainer>
            {loading ? (
              <LoadingContainer>
                <Spinner />
                <LoadingText>Loading signal boxes...</LoadingText>
              </LoadingContainer>
            ) : error ? (
              <ErrorMessage>{error}</ErrorMessage>
            ) : filtered.length === 0 ? (
              <EmptyState>
                <EmptyIcon>🔍</EmptyIcon>
                <EmptyText>No signal boxes found</EmptyText>
                <EmptyHint>Try a different search term</EmptyHint>
              </EmptyState>
            ) : (
              filtered.map((box, i) => (
                <ItemCard key={box.id} delay={i}>
                  <ItemHeader>
                    <ItemName>{box.name}</ItemName>
                    <SuggestBtn onClick={() => openSuggestModal(box)}>
                      Suggest Change
                    </SuggestBtn>
                  </ItemHeader>
                  <PhoneRow>
                    <PhoneItem>
                      <PhoneLabel>Internal</PhoneLabel>
                      {box.internal_number ? (
                        <PhoneNumber href={formatPhone(box.internal_number)}>
                          {box.internal_number}
                        </PhoneNumber>
                      ) : (
                        <NoNumber>N/A</NoNumber>
                      )}
                    </PhoneItem>
                    <PhoneItem>
                      <PhoneLabel>External</PhoneLabel>
                      {box.external_number ? (
                        <PhoneNumber href={formatPhone(box.external_number)}>
                          {box.external_number}
                        </PhoneNumber>
                      ) : (
                        <NoNumber>N/A</NoNumber>
                      )}
                    </PhoneItem>
                  </PhoneRow>
                </ItemCard>
              ))
            )}
          </ListContainer>

          <Divider />

          <Footer>
            <BackButton onClick={onBack}>
              Back to Resources
            </BackButton>
          </Footer>
        </Card>
      </Container>

      {/* Suggest a Change Modal */}
      {editingBox && (
        <ModalOverlay onClick={(e) => { if (e.target === e.currentTarget) closeSuggestModal(); }}>
          <ModalCard>
            <ModalHeader>
              <ModalTitle>Suggest a Change</ModalTitle>
              <ModalSubtitle>{editingBox.name}</ModalSubtitle>
            </ModalHeader>
            <ModalBody>
              {submitSuccess ? (
                <SuccessMessage>
                  Change request submitted successfully. An admin will review it shortly.
                </SuccessMessage>
              ) : (
                <>
                  {submitError && (
                    <ErrorBanner>{submitError}</ErrorBanner>
                  )}

                  <FormGroup>
                    <FormLabel>Internal Number</FormLabel>
                    <CurrentValue>
                      Current: {editingBox.internal_number || 'N/A'}
                    </CurrentValue>
                    <FormInput
                      type="text"
                      placeholder="Suggested internal number..."
                      value={suggestedInternal}
                      onChange={(e) => setSuggestedInternal(e.target.value)}
                    />
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>External Number</FormLabel>
                    <CurrentValue>
                      Current: {editingBox.external_number || 'N/A'}
                    </CurrentValue>
                    <FormInput
                      type="text"
                      placeholder="Suggested external number..."
                      value={suggestedExternal}
                      onChange={(e) => setSuggestedExternal(e.target.value)}
                    />
                  </FormGroup>

                  <FormGroup>
                    <FormLabel>Notes (optional)</FormLabel>
                    <FormTextarea
                      placeholder="Reason for change..."
                      value={changeNotes}
                      onChange={(e) => setChangeNotes(e.target.value)}
                    />
                  </FormGroup>

                  <ModalActions>
                    <CancelButton onClick={closeSuggestModal}>
                      Cancel
                    </CancelButton>
                    <SubmitButton
                      onClick={handleSubmitChange}
                      disabled={submitting}
                    >
                      {submitting ? 'Submitting...' : 'Submit'}
                    </SubmitButton>
                  </ModalActions>
                </>
              )}
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
    </>
  );
};

export default SignalBoxDirectory;
